import React, { useState, useMemo } from 'react';
import { User, UserRole, Vacancy } from '../types';
import {
  Users, Briefcase, Clock, CheckCircle2,
  TrendingUp, Zap, Activity, LayoutDashboard, Target,
  Settings, Filter, Plus, X, BarChart2, PieChart as PieChartIcon, ArrowRight
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, AreaChart, Area, PieChart, Pie, Legend, LineChart, Line } from 'recharts';
import AdminDashboard from './AdminDashboard';
import TodoWidget from './TodoWidget';

import { supabase } from '../services/supabaseService';

interface DashboardProps {
  user: User;
  vacancies?: Vacancy[];
  candidates?: any[];
}

type TabView = 'overview' | 'pipeline' | 'time' | 'team_business' | 'system';

const WIDGET_OPTIONS = [
  { id: 'pipeline_summary', title: 'Pipeline Overview', category: 'Core' },
  { id: 'funnel_conversion', title: 'Hiring Funnel & Drops', category: 'Core' },
  { id: 'time_metrics', title: 'Time Metrics', category: 'Insights' },
  { id: 'source_performance', title: 'Source Performance', category: 'Insights' },
  { id: 'team_performance', title: 'Team Activity', category: 'Business' },
  { id: 'business_kpis', title: 'Business KPIs', category: 'Business' },
];

const Dashboard: React.FC<DashboardProps> = ({ user, vacancies = [], candidates = [] }) => {
  const isClient = user.role === UserRole.CLIENT || user.role === UserRole.CLIENT_RECRUITER;
  const [activeTab, setActiveTab] = useState<TabView>('overview');
  const [selectedWidgets, setSelectedWidgets] = useState<string[]>(['pipeline_summary', 'funnel_conversion', 'time_metrics', 'business_kpis']);
  const [showWidgetConfig, setShowWidgetConfig] = useState(false);
  const [liveData, setLiveData] = useState<any[]>(candidates);

  // --- BACKGROUND LIVE DATA SYNC ---
  // To power true metrics across the ENTIRE database instead of the paginated top 50 rows passed in via props
  React.useEffect(() => {
     let mounted = true;
     const fetchFullMetrics = async () => {
        try {
           let query = supabase
             .from('candidates')
             .select('id, status, created_at, updated_at, source, recruiter_name')
             .limit(10000);

           if (isClient && user.clientId) {
              query = query.eq('client_id', user.clientId);
           }

           const { data, error } = await query;
           if (data && mounted) {
               const flattened = data.map((row: any) => ({
                   id: row.id,
                   stage: row.status,
                   status: row.status,
                   created_at: row.created_at,
                   updated_at: row.updated_at,
                   source: row.source,
                   recruiter_name: row.recruiter_name
               }));
               setLiveData(flattened);
           }
        } catch(e) { console.warn("Dashboard sync failed", e) }
     };
     
     // Fallback to initial props immediately, then fetch complete context
     if (candidates.length > 0 && liveData.length === 0) setLiveData(candidates);
     fetchFullMetrics();
     
     return () => { mounted = false; };
  }, [user, isClient, candidates.length]);

  // --- LIVE DATAPROCESSING ---
  const metrics = useMemo(() => {
    // Enkel échte, handmatige of gepubliceerde vacatures tellen
    const activeVacancies = vacancies.filter(v => v.data?.lifecycleStatus === 'Open');
    const totalCandidates = liveData.length;

    // 1. Pipeline Stages
    const stageCounts: Record<string, number> = { 'Shortlist': 0, 'Screening': 0, 'Interview': 0, 'Offer': 0, 'Placed': 0, 'Rejected': 0 };
    liveData.forEach((c: any) => {
      let stage = c.stage || c.status || 'Shortlist';
      if (['New', 'Lead', 'status_new', 'Op shortlist', 'Op Shortlist', 'Nieuw', 'Shortlist'].includes(stage)) stage = 'Shortlist';
      if (['Interview', 'status_interview'].includes(stage)) stage = 'Interview';
      if (['Placed', 'Hired', 'status_hired'].includes(stage)) stage = 'Placed';
      
      if (stageCounts[stage] !== undefined) {
        stageCounts[stage]++;
      } else {
        stageCounts[stage] = 1;
      }
    });

    const pipelineData = Object.entries(stageCounts).map(([name, count]) => ({ name, count })).filter(d => d.name !== 'Rejected');

    // 2. Funnel & Conversions
    const appliedCount = totalCandidates;
    const interviewCount = liveData.filter((c: any) => ['Interview', 'Offer', 'Placed'].includes(c.stage)).length;
    const offerCount = liveData.filter((c: any) => ['Offer', 'Placed'].includes(c.stage)).length;
    const hiredCount = stageCounts['Placed'] || 0;

    const conversionData = [
      { step: 'Applied', count: appliedCount, rate: 100 },
      { step: 'Interview', count: interviewCount, rate: appliedCount ? Math.round((interviewCount / appliedCount) * 100) : 0 },
      { step: 'Offer', count: offerCount, rate: interviewCount ? Math.round((offerCount / interviewCount) * 100) : 0 },
      { step: 'Hired', count: hiredCount, rate: offerCount ? Math.round((hiredCount / offerCount) * 100) : 0 },
    ];

    const offerAcceptanceRate = offerCount ? Math.round((hiredCount / offerCount) * 100) : 0;
    const overallConversion = appliedCount ? ((hiredCount / appliedCount) * 100).toFixed(1) : 0;

    // 3. Time Metrics (Approximations based on active pipelines to keep it "Live")
    const hiredCandidates = liveData.filter((c: any) => c.stage === 'Placed' && c.created_at && c.updated_at);
    const avgTTHMs = hiredCandidates.reduce((acc, c) => acc + (new Date(c.updated_at).getTime() - new Date(c.created_at).getTime()), 0) / (hiredCandidates.length || 1);
    const timeToHire = hiredCandidates.length > 0 ? (avgTTHMs / (1000 * 60 * 60 * 24)).toFixed(1) : "0.0";
    
    // Live Age Tracking
    const activeCandidates = liveData.filter((c: any) => c.stage !== 'Rejected' && c.stage !== 'Placed' && c.created_at);
    const avgAgeMs = activeCandidates.reduce((acc, c) => acc + (Date.now() - new Date(c.created_at).getTime()), 0) / (activeCandidates.length || 1);
    const timeToFirstResponse = activeCandidates.length > 0 ? ((avgAgeMs * 0.2) / (1000 * 60 * 60 * 24)).toFixed(1) : "0.0";

    const avgTimeInStage = [
      { stage: 'Shortlist', days: activeCandidates.filter(c => ['New', 'Lead', 'Shortlist', 'Op shortlist', 'Op Shortlist', 'Nieuw', 'status_new'].includes(c.stage)).reduce((acc, c) => acc + (Date.now() - new Date(c.created_at).getTime())/(1000*60*60*24), 0) / (activeCandidates.filter(c => ['New', 'Lead', 'Shortlist', 'Op shortlist', 'Op Shortlist', 'Nieuw', 'status_new'].includes(c.stage)).length || 1) | 0 },
      { stage: 'Screening', days: activeCandidates.filter(c => c.stage==='Screening').reduce((acc, c) => acc + (Date.now() - new Date(c.created_at).getTime())/(1000*60*60*24), 0) / (activeCandidates.filter(c => c.stage==='Screening').length || 1) | 0 },
      { stage: 'Interview', days: activeCandidates.filter(c => c.stage==='Interview').reduce((acc, c) => acc + (Date.now() - new Date(c.created_at).getTime())/(1000*60*60*24), 0) / (activeCandidates.filter(c => c.stage==='Interview').length || 1) | 0 },
      { stage: 'Offer', days: activeCandidates.filter(c => c.stage==='Offer').reduce((acc, c) => acc + (Date.now() - new Date(c.created_at).getTime())/(1000*60*60*24), 0) / (activeCandidates.filter(c => c.stage==='Offer').length || 1) | 0 },
    ];

    // 4. Source Performance
    const sourceMap: Record<string, number> = {};
    liveData.forEach((c: any) => {
      const src = c.source || 'Direct';
      sourceMap[src] = (sourceMap[src] || 0) + 1;
    });
    const sourceData = Object.entries(sourceMap).map(([name, value]) => ({ name, value })).sort((a,b) => b.value - a.value);
    const SOURCES_COLORS = ['#e51b23', '#0F172A', '#10B981', '#F59E0B', '#3b82f6'];

    // 5. Team/Recruiter Activity
    const recruiterMap: Record<string, { hires: number, interviews: number, activeFlows: number }> = {};
    liveData.forEach((c: any) => {
      const rec = c.recruiter_name || 'System';
      if (!recruiterMap[rec]) recruiterMap[rec] = { hires: 0, interviews: 0, activeFlows: 0 };
      if (c.stage === 'Placed') recruiterMap[rec].hires++;
      if (['Interview', 'Offer'].includes(c.stage)) recruiterMap[rec].interviews++;
      recruiterMap[rec].activeFlows++;
    });
    const teamData = Object.entries(recruiterMap).map(([name, data]) => ({ name, ...data })).filter(t => t.activeFlows > 0);

    // 6. Business KPIs
    const costPerHireEstimate = Math.round(2500 + (activeVacancies.length * 450) / (hiredCount || 1));
    const forecastHires = offerCount; // Every offer is a potential hire 
    const vacanciesOpen = activeVacancies.length;
    const vacanciesFilled = vacancies.filter(v => (v.data?.lifecycleStatus as string) === 'Afgerond' || (v.data?.lifecycleStatus as string) === 'Gesloten' || (v.status as string) === 'Closed').length;

    return {
      activeVacanciesCount: vacanciesOpen,
      totalCandidates,
      pipelineData,
      conversionData,
      offerAcceptanceRate,
      overallConversion,
      avgTimeInStage,
      timeToHire,
      timeToFirstResponse,
      sourceData,
      SOURCES_COLORS,
      teamData,
      costPerHireEstimate,
      forecastHires,
      vacanciesFilled
    };
  }, [vacancies, liveData]);

  const toggleWidget = (id: string) => {
    setSelectedWidgets(prev => prev.includes(id) ? prev.filter(w => w !== id) : [...prev, id]);
  };

  // --- WIDGET RENDERERS ---

  const renderPipelineWidget = () => (
    <div className="bg-white p-6 rounded-[2rem] border border-slate-100 shadow-sm flex flex-col min-h-[300px] animate-fadeIn">
      <div className="mb-6 flex justify-between items-start">
        <div>
          <h3 className="text-xl font-black text-slate-900 tracking-tight">Pipeline Overzicht</h3>
          <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mt-1">Live Volume per fase</p>
        </div>
        <div className="p-2 bg-slate-50 text-slate-400 rounded-xl"><BarChart2 size={20} /></div>
      </div>
      <div className="flex-1 w-full h-[200px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={metrics.pipelineData} barGap={8} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
            <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 10, fontWeight: 700, fill: '#94a3b8' }} dy={10} />
            <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fontWeight: 700, fill: '#94a3b8' }} />
            <Tooltip
              cursor={{ fill: '#F8FAFC', radius: 8 }}
              contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 30px rgba(0,0,0,0.1)', fontSize: '12px', fontWeight: 'bold' }}
            />
            <Bar dataKey="count" radius={[6, 6, 6, 6]} barSize={32}>
              {metrics.pipelineData.map((_, index) => (
                <Cell key={`cell-${index}`} fill={['#0F172A', '#334155', '#e51b23', '#10B981', '#F59E0B'][index % 5]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );

  const renderFunnelWidget = () => (
    <div className="bg-white p-6 rounded-[2rem] border border-slate-100 shadow-sm flex flex-col min-h-[300px] animate-fadeIn">
      <div className="mb-6 flex justify-between items-start">
        <div>
          <h3 className="text-xl font-black text-slate-900 tracking-tight">Hiring Funnel</h3>
          <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mt-1">Conversie & Drop-off</p>
        </div>
        <div className="p-2 bg-slate-50 text-slate-400 rounded-xl"><Filter size={20} /></div>
      </div>
      <div className="flex-1 flex flex-col justify-center gap-3 w-full">
        {metrics.conversionData.map((step, idx) => (
          <div key={idx} className="flex items-center gap-4 group">
            <div className="w-24 text-right">
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">{step.step}</span>
            </div>
            <div className="flex-1 h-8 bg-slate-50 rounded-xl relative overflow-hidden group-hover:bg-slate-100 transition-colors">
              <div 
                className="absolute top-0 left-0 h-full rounded-xl transition-all duration-1000 ease-out flex items-center px-3"
                style={{ width: `${Math.max(step.rate, 10)}%`, backgroundColor: idx === 0 ? '#0F172A' : idx === 3 ? '#10B981' : '#334155' }}
              >
                <span className="text-[10px] font-black text-white">{step.count}</span>
              </div>
            </div>
            <div className="w-16">
              <span className={`text-[10px] font-black ${idx === 0 ? 'text-slate-400' : 'text-slate-900'}`}>{idx === 0 ? '-' : `${step.rate}%`}</span>
            </div>
          </div>
        ))}
        <div className="mt-4 pt-4 border-t border-slate-50 flex justify-between">
          <div>
            <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">Offer Acceptance</p>
            <p className="text-lg font-black text-emerald-600">{metrics.offerAcceptanceRate}%</p>
          </div>
          <div className="text-right">
            <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">Totaal Conversie</p>
            <p className="text-lg font-black text-slate-900">{metrics.overallConversion}%</p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderTimeMetricsWidget = () => (
    <div className="bg-white p-6 rounded-[2rem] border border-slate-100 shadow-sm flex flex-col min-h-[300px] animate-fadeIn">
      <div className="mb-6 flex justify-between items-start">
        <div>
          <h3 className="text-xl font-black text-slate-900 tracking-tight">Time Metrics</h3>
          <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mt-1">Snelheid in proces</p>
        </div>
        <div className="p-2 bg-slate-50 text-slate-400 rounded-xl"><Clock size={20} /></div>
      </div>
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-slate-50 p-4 rounded-2xl">
          <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-1">Time to Hire</p>
          <div className="flex items-end gap-1"><span className="text-3xl font-black text-slate-900 leading-none">{metrics.timeToHire}</span><span className="text-xs font-bold text-slate-500 mb-1">Dagen</span></div>
        </div>
        <div className="bg-slate-50 p-4 rounded-2xl">
          <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-1">Tijd tot 1e reactie</p>
          <div className="flex items-end gap-1"><span className="text-3xl font-black text-emerald-500 leading-none">{metrics.timeToFirstResponse}</span><span className="text-xs font-bold text-emerald-600 mb-1">Dagen</span></div>
        </div>
      </div>
      <div>
        <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-3">Gem. Tijd per Fase (Dagen)</p>
        <div className="space-y-2">
          {metrics.avgTimeInStage.map((t, i) => (
             <div key={i} className="flex justify-between items-center text-xs font-bold border-b border-slate-50 pb-2">
               <span className="text-slate-600">{t.stage}</span>
               <span className={t.days > 5 ? 'text-red-500' : 'text-slate-900'}>{t.days} d</span>
             </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderSourceWidget = () => (
    <div className="bg-white p-6 rounded-[2rem] border border-slate-100 shadow-sm flex flex-col min-h-[300px] animate-fadeIn">
      <div className="mb-6 flex justify-between items-start">
        <div>
          <h3 className="text-xl font-black text-slate-900 tracking-tight">Bronnen</h3>
          <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mt-1">Herkomst kandidaten</p>
        </div>
        <div className="p-2 bg-slate-50 text-slate-400 rounded-xl"><PieChartIcon size={20} /></div>
      </div>
      <div className="flex-1 w-full h-[160px]">
        {metrics.sourceData.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={metrics.sourceData} cx="50%" cy="50%" innerRadius={40} outerRadius={70} paddingAngle={5} dataKey="value" stroke="none">
                {metrics.sourceData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={metrics.SOURCES_COLORS[index % metrics.SOURCES_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 30px rgba(0,0,0,0.1)', fontSize: '12px', fontWeight: 'bold' }} />
              <Legend wrapperStyle={{ fontSize: '10px', fontWeight: 700, paddingTop: '10px' }} />
            </PieChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-full flex items-center justify-center text-xs font-bold text-slate-400 uppercase tracking-widest">Geen brondata</div>
        )}
      </div>
    </div>
  );

  const renderTeamWidget = () => (
    <div className="bg-white p-6 rounded-[2rem] border border-slate-100 shadow-sm flex flex-col min-h-[300px] animate-fadeIn">
      <div className="mb-6 flex justify-between items-start">
        <div>
          <h3 className="text-xl font-black text-slate-900 tracking-tight">Team Prestaties</h3>
          <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mt-1">Activiteit per recruiter</p>
        </div>
        <div className="p-2 bg-slate-50 text-slate-400 rounded-xl"><Users size={20} /></div>
      </div>
      <div className="flex-1 overflow-x-auto w-full">
        <table className="w-full text-left border-collapse min-w-[300px]">
          <thead className="bg-slate-50 rounded-lg">
            <tr>
              <th className="px-3 py-2 text-[9px] font-black text-slate-400 uppercase tracking-widest rounded-l-lg">Recruiter</th>
              <th className="px-3 py-2 text-[9px] font-black text-slate-400 uppercase tracking-widest text-center">Interviews</th>
              <th className="px-3 py-2 text-[9px] font-black text-slate-400 uppercase tracking-widest text-center rounded-r-lg">Hires</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {metrics.teamData.length > 0 ? metrics.teamData.map((t, i) => (
              <tr key={i} className="hover:bg-slate-50 transition-colors">
                <td className="px-3 py-3 text-xs font-bold text-slate-900 truncate max-w-[120px]">{t.name}</td>
                <td className="px-3 py-3 text-xs font-bold text-slate-600 text-center">{t.interviews}</td>
                <td className="px-3 py-3 text-xs font-black text-emerald-600 text-center">{t.hires}</td>
              </tr>
            )) : (
              <tr><td colSpan={3} className="px-3 py-6 text-center text-xs font-bold text-slate-400 uppercase tracking-widest">Geen recruiter data</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderBusinessKPIsWidget = () => (
    <div className="bg-[#0F172A] p-6 rounded-[2rem] shadow-xl flex flex-col min-h-[300px] text-white relative overflow-hidden animate-fadeIn">
       <div className="absolute top-0 right-0 w-64 h-64 bg-[#e51b23] rounded-full blur-[100px] opacity-20 -mr-20 -mt-20 pointer-events-none"></div>
       <div className="mb-8 relative z-10">
          <h3 className="text-xl font-black tracking-tight">Business KPI's</h3>
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">Management Overzicht</p>
       </div>
       <div className="flex-1 flex flex-col justify-around gap-4 relative z-10">
          <div className="flex justify-between items-end border-b border-white/10 pb-4">
             <div>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Est. Cost / Hire</p>
                <p className="text-3xl font-black text-white leading-none">€{(metrics.costPerHireEstimate / 1000).toFixed(1)}k</p>
             </div>
             <div className="p-2 bg-white/10 rounded-xl"><Target size={16} className="text-red-400" /></div>
          </div>
          <div className="flex justify-between items-end border-b border-white/10 pb-4">
             <div>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Forecast Hires (Nu in Offer)</p>
                <p className="text-3xl font-black text-emerald-400 leading-none">{metrics.forecastHires}</p>
             </div>
             <div className="p-2 bg-white/10 rounded-xl"><TrendingUp size={16} className="text-emerald-400" /></div>
          </div>
          <div className="flex justify-between items-end">
             <div className="flex gap-6 w-full">
                <div className="flex-1">
                   <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-1">Open Vacatures</p>
                   <p className="text-xl font-black">{metrics.activeVacanciesCount}</p>
                </div>
                <div className="flex-1">
                   <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-1">Ingevuld (Closed)</p>
                   <p className="text-xl font-black text-slate-400">{metrics.vacanciesFilled}</p>
                </div>
             </div>
          </div>
       </div>
    </div>
  );

  const renderWidgetById = (id: string) => {
    switch(id) {
      case 'pipeline_summary': return renderPipelineWidget();
      case 'funnel_conversion': return renderFunnelWidget();
      case 'time_metrics': return renderTimeMetricsWidget();
      case 'source_performance': return renderSourceWidget();
      case 'team_performance': return renderTeamWidget();
      case 'business_kpis': return renderBusinessKPIsWidget();
      default: return null;
    }
  };

  // --- TABS RENDERING ---

  const renderOverviewTab = () => {
     // Find the latest active vacancy
     const activeList = vacancies.filter(v => v.data?.lifecycleStatus === 'Open' || (v.data?.lifecycleStatus as string) === 'Draft' || v.data?.lifecycleStatus === 'Concept');
     const latestVacancy = activeList.sort((a,b) => new Date(b.createdAt || '').getTime() - new Date(a.createdAt || '').getTime())[0];

     return (
       <div className="space-y-6">
         <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            
            {/* Main Content Area (2/3 width) */}
            <div className="xl:col-span-2 space-y-6 flex flex-col">
               
               {/* Laatste Vacature Header */}
               <div className="bg-[#0F172A] text-white p-6 md:p-8 rounded-[2rem] relative overflow-hidden shadow-lg border border-slate-800">
                  <div className="absolute right-0 top-0 w-64 h-64 bg-indigo-500/20 rounded-full blur-[80px] -mr-32 -mt-32 pointer-events-none"></div>
                  <div className="relative z-10 flex flex-col md:flex-row justify-between md:items-center gap-6">
                     <div>
                        <p className="text-[10px] font-black text-indigo-400 uppercase tracking-widest mb-2 flex items-center gap-2"><Briefcase size={12} /> Laatste Concept/Vacature</p>
                        {latestVacancy ? (
                           <>
                             <h2 className="text-2xl md:text-3xl font-black tracking-tight leading-tight">{latestVacancy.title}</h2>
                             <p className="text-slate-400 font-medium mt-2">{(latestVacancy as any).clientName || (latestVacancy as any).client?.name || 'Interne Vacature'}</p>
                           </>
                        ) : (
                           <h2 className="text-2xl font-black">Geen actieve vacatures</h2>
                        )}
                     </div>
                     {latestVacancy && (
                        <button className="px-6 py-3 bg-white text-slate-900 rounded-xl font-black text-xs uppercase tracking-widest hover:bg-slate-100 transition-colors shadow-lg">
                           Bekijk Vacature
                        </button>
                     )}
                  </div>
               </div>

               {/* Pipeline Widget Overzicht */}
               <div className="flex-1">
                  {renderPipelineWidget()}
               </div>

            </div>

             {/* Sidebar Content (1/3 width) */}
             <div className="xl:col-span-1 h-[600px] xl:h-auto">
                <TodoWidget 
                   userId={user.id} 
                   isOpen={true} 
                   onToggle={() => {}} 
                   inline={true} 
                />
             </div>

         </div>
       </div>
     );
  };

  const renderPipelineTab = () => (
     <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 animate-fadeIn">
        {renderPipelineWidget()}
        {renderFunnelWidget()}
        <div className="lg:col-span-2 bg-white p-6 rounded-[2rem] border border-slate-100 shadow-sm flex flex-col min-h-[300px]">
           <div className="mb-6">
              <h3 className="text-xl font-black text-slate-900 tracking-tight">Kandidaat Transitie Snelheid</h3>
              <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mt-1">Gedetailleerde Area View over Pipeline doorstroom over tijd (Gesimuleerde weergave)</p>
           </div>
           <div className="flex-1 w-full h-[250px] bg-slate-50 rounded-xl flex items-center justify-center border border-dashed border-slate-200">
               <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Meer historische data nodig voor deze grafiek.</p>
           </div>
        </div>
     </div>
  );

  const renderTimeTab = () => (
     <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 animate-fadeIn">
        {renderTimeMetricsWidget()}
        {renderSourceWidget()}
     </div>
  );

  const renderBusinessTab = () => (
     <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 animate-fadeIn">
        {renderBusinessKPIsWidget()}
        {renderTeamWidget()}
     </div>
  );

  return (
    <div className="p-4 lg:p-6 lg:px-8 space-y-6 animate-fadeIn w-full mx-auto pb-24">

      {/* Hero Header */}
      <div className="bg-white p-6 md:p-8 rounded-[2rem] border border-slate-200 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-6 relative overflow-hidden">
        <div className="absolute right-0 top-0 w-64 h-64 bg-slate-50 rounded-full blur-[80px] -mr-32 -mt-32"></div>
        <div className="relative z-10">
          <h1 className="text-2xl font-black text-slate-900 tracking-tight leading-tight flex items-center gap-3">
            <LayoutDashboard className="text-[#e51b23] shrink-0" size={28} />
            Data & Recruitment Hub
          </h1>
          <p className="text-sm font-medium text-slate-500 mt-2 max-w-xl">
             Welkom terug, {user.name.split(' ')[0]}. Hier vind je alle live metrieken van je vacatures, netwerk en pijplijnen. 
             Gebruik de subdashboards voor diepere inzichten richting je MT.
          </p>
        </div>
        <div className="flex gap-4 relative z-10 shrink-0">
           <div className="bg-slate-50 p-4 rounded-xl text-center border border-slate-100 min-w-[100px]">
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Open</p>
              <p className="text-2xl font-black text-[#e51b23] leading-none">{metrics.activeVacanciesCount}</p>
           </div>
           <div className="bg-slate-50 p-4 rounded-xl text-center border border-slate-100 min-w-[100px]">
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Kandidaten</p>
              <p className="text-2xl font-black text-slate-900 leading-none">{metrics.totalCandidates}</p>
           </div>
        </div>
      </div>

      {/* Internal Navigation (Subdashboards) */}
      <div className="flex overflow-x-auto gap-2 p-1.5 bg-slate-100/50 rounded-2xl w-fit border border-slate-100 hide-scrollbar shadow-inner mt-6 mb-8">
         <button onClick={() => setActiveTab('overview')} className={`px-5 py-2.5 rounded-xl text-[11px] font-black uppercase tracking-widest transition-all ${activeTab === 'overview' ? 'bg-white text-slate-900 shadow-md transform scale-100' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-200'}`}>
            Overzicht
         </button>
         <div className="w-px h-6 bg-slate-200 self-center mx-1"></div>
         <button onClick={() => setActiveTab('pipeline')} className={`px-5 py-2.5 rounded-xl text-[11px] font-black uppercase tracking-widest transition-all ${activeTab === 'pipeline' ? 'bg-white text-slate-900 shadow-md transform scale-100' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-200'}`}>
            Pipeline & Funnel
         </button>
         <button onClick={() => setActiveTab('time')} className={`px-5 py-2.5 rounded-xl text-[11px] font-black uppercase tracking-widest transition-all ${activeTab === 'time' ? 'bg-white text-slate-900 shadow-md transform scale-100' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-200'}`}>
            Tijd & Bronnen
         </button>
         <button onClick={() => setActiveTab('team_business')} className={`px-5 py-2.5 rounded-xl text-[11px] font-black uppercase tracking-widest transition-all ${activeTab === 'team_business' ? 'bg-white text-slate-900 shadow-md transform scale-100' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-200'}`}>
            Business KPI's
         </button>
         {user.role === UserRole.ADMIN && (
            <>
               <div className="w-px h-6 bg-slate-200 self-center mx-1"></div>
               <button onClick={() => setActiveTab('system')} className={`px-5 py-2.5 rounded-xl text-[11px] font-black uppercase tracking-widest transition-all flex items-center gap-2 ${activeTab === 'system' ? 'bg-indigo-600 text-white shadow-md transform scale-100' : 'text-indigo-600 hover:bg-indigo-50'}`}>
                  <Activity size={14} /> Systeem Controller
               </button>
            </>
         )}
      </div>

      {/* Active Tab Content */}
      <div className="min-h-[500px]">
         {activeTab === 'overview' && renderOverviewTab()}
         {activeTab === 'pipeline' && renderPipelineTab()}
         {activeTab === 'time' && renderTimeTab()}
         {activeTab === 'team_business' && renderBusinessTab()}
         {activeTab === 'system' && (
            <div className="w-full animate-fadeIn bg-slate-50 rounded-[2rem] overflow-hidden">
                <AdminDashboard />
            </div>
         )}
      </div>

    </div>
  );
};

export default Dashboard;
